
    import { useState, useEffect, useCallback } from 'react';
    import { getSupabase, ensureSupabaseInitialized } from '@/lib/supabaseClient.js';
    import { useToast } from "@/components/ui/use-toast";

    export const useProfileData = (userId) => {
      const [profile, setProfile] = useState(null);
      const [loadingProfile, setLoadingProfile] = useState(true);
      const [errorProfile, setErrorProfile] = useState(null);
      const [isSavingProfile, setIsSavingProfile] = useState(false);
      const { toast } = useToast();

      const fetchProfile = useCallback(async () => {
        try {
          await ensureSupabaseInitialized(); 
          const supabase = getSupabase();
          if (!userId || !supabase) {
            setProfile(null);
            setLoadingProfile(false);
            if (!supabase) console.warn("[useProfileData] Supabase client not available for fetchProfile.");
            return;
          }
          setLoadingProfile(true);
          setErrorProfile(null);
          
          const { data, error } = await supabase
            .from('profiles')
            .select(`
              id,
              first_name,
              last_name,
              role,
              is_active,
              avatar_url,
              terms_agreed,
              terms_agreed_at,
              terms_version_agreed,
              profile_complete,
              has_added_credit_score,
              has_added_accounts,
              has_set_goal,
              has_completed_onboarding_tour,
              overall_onboarding_complete,
              updated_at
            `)
            .eq('id', userId)
            .single();

          if (error && error.code !== 'PGRST116') { 
            throw error;
          }
          setProfile(data);
        } catch (err) {
          console.error("[useProfileData] Error fetching profile:", err);
          setErrorProfile(err.message || "Could not load profile.");
          setProfile(null);
        } finally {
          setLoadingProfile(false);
        }
      }, [userId]); 

      useEffect(() => {
        fetchProfile();
      }, [fetchProfile]);

      const updateUserProfile = useCallback(async (updates) => {
        try {
            await ensureSupabaseInitialized();
            const supabase = getSupabase();
            if (!userId || !supabase) {
              toast({ title: "Error", description: "User not logged in or Supabase not available.", variant: "destructive" });
              if (!supabase) console.warn("[useProfileData] Supabase client not available for updateUserProfile.");
              return null;
            }
            setIsSavingProfile(true);
            
            const { data, error } = await supabase
              .from('profiles')
              .update({ ...updates, updated_at: new Date().toISOString() })
              .eq('id', userId)
              .select()
              .single();

            if (error) {
              if (error.message.includes("overall_onboarding_complete") && error.code === "PGRST204") {
                console.warn("[useProfileData] 'overall_onboarding_complete' column might be missing or Supabase schema cache needs refresh. Attempting to refetch profile.");
                await fetchProfile(); 
              }
              throw error;
            }
            
            setProfile(data);
            return data;
        } catch (err) {
            console.error("[useProfileData] Error updating profile:", err);
            toast({ title: "Update Failed", description: err.message || "Could not update profile.", variant: "destructive" });
            throw err;
        } finally {
            setIsSavingProfile(false);
        }
      }, [userId, toast, fetchProfile]); 
      
      useEffect(() => {
        let channel = null;
        let isMounted = true;

        const setupSubscription = async () => {
            try {
                await ensureSupabaseInitialized();
                const supabase = getSupabase();

                if (!isMounted || !userId || !supabase) {
                    return;
                }

                if (typeof window === 'undefined' || typeof window.WebSocket === 'undefined') {
                    console.warn(`[useProfileData] Browser WebSocket API not available. Skipping real-time subscription for profile for user ${userId}.`);
                    return;
                }
                
                if (!supabase.realtime || typeof supabase.getChannels !== 'function') { 
                    console.warn(`[useProfileData] Supabase Realtime client for user ${userId} seems uninitialized or misconfigured (no getChannels method). Skipping real-time subscription.`);
                    return;
                }
                
                channel = supabase
                  .channel(`profile-changes-${userId}-${Math.random().toString(36).substring(7)}`) 
                  .on(
                    'postgres_changes',
                    { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
                    (payload) => {
                      if (!isMounted) return;
                      if (payload.new && payload.new.id === userId) {
                        setProfile(prevProfile => ({ ...prevProfile, ...payload.new }));
                      }
                    }
                  )
                  .subscribe((status, err) => {
                    if (!isMounted) return;
                    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || err) {
                      console.error(`[useProfileData] Subscription error for profile ${userId}:`, status, err);
                    }
                  });
            } catch (error) {
                 if (!isMounted) return;
                 console.error(`[useProfileData] Error setting up subscription for profile ${userId}:`, error);
            }
        };

        setupSubscription();
            
        return () => {
          isMounted = false;
          if (channel) {
            const supabase = getSupabase();
            if (supabase && supabase.removeChannel) {
                supabase.removeChannel(channel).catch(removeError => 
                  console.error("[useProfileData] Error removing profile channel:", removeError)
                );
            }
          }
        };
      }, [userId]); // Only depend on userId, not the entire fetchProfile function

      return {
        profile,
        loadingProfile,
        errorProfile,
        updateUserProfile,
        isSavingProfile,
        refetchProfile: fetchProfile,
      };
    };
  
